import type * as geojson from 'geojson';
import {useEffect, useRef} from 'react';

import {recordEvent} from 'src/components/Analytics';
import {
  ApiForecastTimeSeries,
  ForecastTimeSeriesQuery,
  batchFetchForecastTimeSeries,
} from 'src/lib/api/series';

import {GridSampling, isDynamicalSource} from './datasets';
import {DynamicalUnavailableError} from './errors';
import {fetchDynamicalForecastTimeSeries} from './fetchForecast';
import {
  ParitySummary,
  compareTimeSeries,
  gfsPrecipAllowPoint,
  productionTolerances,
  summarizeReport,
} from './parity';

/**
 * Shadow comparison: while the HF API keeps serving the user's forecasts, read
 * the same query from dynamical.org in the background, and record to PostHog
 * (event `dynamical_shadow_parity`) both how far the two diverge and how long
 * each path took. It measures the direct path's production accuracy and
 * performance without users ever seeing a direct read, so we can decide when to
 * flip DYNAMICAL_DIRECT_WEATHER on for real.
 *
 * Accuracy rate: outcome=match / (match + mismatch) is the fraction within the
 * same tolerances the parity sweep proves. Performance: `apiMs` vs `directMs`
 * are measured on the same single query, back to back, so they compare fairly.
 * `mismatch`/`error` events carry the page URL and query params to reproduce.
 *
 * To keep the head-to-head honest the comparison issues its own single-query API
 * fetch (timed) rather than reusing the user's already-loaded batch response —
 * a batch latency isn't comparable to a per-query direct read. That extra API
 * call is why the whole thing is sampled; see SHADOW_SAMPLE_RATE.
 */

/**
 * Fraction of eligible queries to actually shadow-compare, within a session that
 * DYNAMICAL_SHADOW_COMPARISON has already opted in. Each sampled query issues one
 * background API fetch and one dynamical.org read. Held at 1 so test-group
 * sessions compare every eligible query and accumulate data fast; the audience
 * is already bounded to a 10% slice of customer sessions (plus internal) by
 * SHADOW_SESSION_SAMPLE_RATE in flag.ts.
 */
const SHADOW_SAMPLE_RATE = 1;

type ApiOptions = {isStaging: boolean; contextOrgId?: string};
type Timing = {apiMs?: number; directMs?: number};

function isShadowEligible(query: ForecastTimeSeriesQuery): boolean {
  return (
    isDynamicalSource(query.source) &&
    !query.includeFlowContributingAreas &&
    !!query.initializationTimes?.length
  );
}

function nowMs(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

function round(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 1e4) / 1e4 : 0;
}

function currentDashboardUrl(): string {
  try {
    return window.location.href;
  } catch {
    return '';
  }
}

function baseProps(query: ForecastTimeSeriesQuery, sampling: GridSampling, timing: Timing) {
  return {
    source: query.source,
    sampling,
    section: query.section ?? 'none',
    timeAggregation: query.timeAggregation ?? 'none',
    aggregationMethod: query.aggregationMethod ?? 'none',
    rateVolumeMode: query.rateVolumeMode ?? 'none',
    unitSystem: query.unitSystem ?? 'none',
    initCount: query.initializationTimes?.length ?? 0,
    ...(timing.apiMs != null ? {apiMs: timing.apiMs} : {}),
    ...(timing.directMs != null ? {directMs: timing.directMs} : {}),
    ...(timing.apiMs != null && timing.directMs != null
      ? {speedupMs: timing.apiMs - timing.directMs}
      : {}),
  };
}

function initTimesLabel(query: ForecastTimeSeriesQuery): string {
  return (query.initializationTimes ?? []).map((d) => d.toISOString()).join(',');
}

/**
 * What the API response held for the requested inits. Recorded on `unavailable`
 * so the dashboard can tell a genuine ingestion/coverage gap (API served the
 * init, direct path lacked it → apiHadRequestedInit=true) apart from a query the
 * API itself couldn't serve either — a distinction the parity report would carry
 * on match/mismatch but that the unavailable path bails out before building.
 */
function apiInitCoverage(
  query: ForecastTimeSeriesQuery,
  apiResult: ApiForecastTimeSeries
): {apiInitCount: number; apiHadRequestedInit: boolean} {
  const apiInitMs = new Set(apiResult.forecasts.map((f) => f.initializationTime.getTime()));
  const requested = query.initializationTimes ?? [];
  return {
    apiInitCount: apiInitMs.size,
    apiHadRequestedInit: requested.length > 0 && requested.every((d) => apiInitMs.has(d.getTime())),
  };
}

function recordComparison(
  query: ForecastTimeSeriesQuery,
  sampling: GridSampling,
  outcome: 'match' | 'mismatch',
  summary: ParitySummary,
  timing: Timing
): void {
  const props: {[key: string]: string | number | boolean} = {
    ...baseProps(query, sampling, timing),
    outcome,
    maxAbsDiff: round(summary.maxAbsDiff),
    maxRelDiff: round(summary.maxRelDiff),
    tempMaxAbsDiff: round(summary.byColumn.temperature?.maxAbsDiff ?? 0),
    precipMaxAbsDiff: round(summary.byColumn.precipitation?.maxAbsDiff ?? 0),
    comparedPoints: summary.comparedPoints,
    allowlistedPoints: summary.allowlistedPoints,
    failurePoints: summary.failurePoints,
    nullMismatches: summary.nullMismatches,
    missingFromDirect: summary.missingFromDirect,
    extraInDirect: summary.extraInDirect,
    initOnlyInApi: summary.initOnlyInApi,
    initOnlyInDirect: summary.initOnlyInDirect,
    unitMismatch: summary.unitMismatch,
    validTimeRangeMismatch: summary.validTimeRangeMismatch,
  };
  if (outcome === 'mismatch') {
    // Everything needed to reopen the exact chart and inspect the divergence.
    props.dashboardUrl = currentDashboardUrl();
    props.siteId = query.siteId;
    props.projectId = query.projectId ?? 'none';
    props.initTimes = initTimesLabel(query);
    props.firstFailure = summary.firstFailure ?? 'none';
  }
  recordEvent('dynamical_shadow_parity', props);
}

function recordUnservable(
  query: ForecastTimeSeriesQuery,
  sampling: GridSampling,
  outcome: 'unavailable' | 'error',
  error: unknown,
  timing: Timing,
  // Present only once the API fetch has returned; the API-failure path has no
  // response to describe.
  apiResult?: ApiForecastTimeSeries
): void {
  recordEvent('dynamical_shadow_parity', {
    ...baseProps(query, sampling, timing),
    outcome,
    dashboardUrl: currentDashboardUrl(),
    siteId: query.siteId,
    projectId: query.projectId ?? 'none',
    initTimes: initTimesLabel(query),
    reason: error instanceof Error ? error.message : String(error),
    ...(apiResult ? apiInitCoverage(query, apiResult) : {}),
  });
}

async function compareQuery(
  query: ForecastTimeSeriesQuery,
  geometry: geojson.Geometry,
  localTimeZone: string,
  sampling: GridSampling,
  apiOptions: ApiOptions
): Promise<void> {
  const timing: Timing = {};
  let apiResult: ApiForecastTimeSeries;
  const apiStart = nowMs();
  try {
    [apiResult] = await batchFetchForecastTimeSeries([query], apiOptions);
    timing.apiMs = Math.round(nowMs() - apiStart);
  } catch (error) {
    // The API is the source of truth; if it fails there's nothing to compare
    // against, so this measures neither path — just note it and bail.
    recordUnservable(query, sampling, 'error', error, timing);
    return;
  }
  if (!apiResult) {
    return;
  }

  const directStart = nowMs();
  try {
    const direct = await fetchDynamicalForecastTimeSeries(query, geometry, localTimeZone, sampling);
    timing.directMs = Math.round(nowMs() - directStart);
    const report = compareTimeSeries(
      apiResult,
      direct,
      productionTolerances(query.source, query.rateVolumeMode),
      {source: query.source, allowPoint: gfsPrecipAllowPoint(query.source, query.timeAggregation)}
    );
    recordComparison(
      query,
      sampling,
      report.ok ? 'match' : 'mismatch',
      summarizeReport(report),
      timing
    );
  } catch (error) {
    timing.directMs = Math.round(nowMs() - directStart);
    // A DynamicalUnavailableError is the direct path correctly declining a query
    // it can't serve (init not in store, site off-grid) — not a correctness
    // defect, so it's tracked as `unavailable` and kept out of the parity rate.
    recordUnservable(
      query,
      sampling,
      error instanceof DynamicalUnavailableError ? 'unavailable' : 'error',
      error,
      timing,
      apiResult
    );
  }
}

async function runShadowComparison(
  queries: ForecastTimeSeriesQuery[],
  geometry: geojson.Geometry,
  localTimeZone: string,
  sampling: GridSampling,
  apiOptions: ApiOptions
): Promise<void> {
  for (const query of queries) {
    if (!isShadowEligible(query) || Math.random() >= SHADOW_SAMPLE_RATE) {
      continue;
    }
    await compareQuery(query, geometry, localTimeZone, sampling, apiOptions);
  }
}

/** Stable signature of the queries so the comparison runs once per selection. */
function queriesSignature(queries: ForecastTimeSeriesQuery[]): string {
  return queries
    .filter(isShadowEligible)
    .map((q) =>
      [
        q.source,
        q.siteId,
        (q.initializationTimes ?? []).map((d) => d.getTime()).join('-'),
        q.timeAggregation,
        q.aggregationMethod,
        q.rateVolumeMode,
        q.unitSystem,
        q.section,
      ].join(':')
    )
    .join('|');
}

/**
 * Runs the background parity + latency comparison once per query selection.
 * Purely a side effect: it never blocks or alters what the caller renders, runs
 * only after the geometry is available, and swallows every failure into
 * telemetry.
 */
export function useDynamicalShadowComparison(params: {
  enabled: boolean;
  queries: ForecastTimeSeriesQuery[];
  geometry: geojson.Geometry | undefined;
  localTimeZone: string;
  sampling: GridSampling;
  isStaging: boolean;
}): void {
  const {enabled, queries, geometry, localTimeZone, sampling, isStaging} = params;
  const signature = queriesSignature(queries);
  // Compare each distinct selection at most once per mount; geometry-ref churn
  // (window-focus refetch returning equal data) must not re-issue the reads.
  const comparedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !geometry || !signature || comparedRef.current === signature) {
      return;
    }
    comparedRef.current = signature;
    void runShadowComparison(queries, geometry, localTimeZone, sampling, {isStaging});
  }, [enabled, geometry, signature, queries, localTimeZone, sampling, isStaging]);
}

export const shadowCompareTestExports = {isShadowEligible, runShadowComparison, SHADOW_SAMPLE_RATE};
